import { AfterViewInit, Component, Inject, OnInit, QueryList, ViewChildren, ChangeDetectorRef } from "@angular/core"
import { Router } from "@angular/router"
import { detect, DetectionResult } from "@cof/scan-sdk-js";
import { newRelicEvent } from "src/app/common"
import { IntakeFormComponent } from "src/app/components/intake-form/intake-form.component"
import { SubheaderContent } from "src/app/components/subheader/SubheaderContent"
import { ErrorLoggingLevel } from "src/app/enums/error-logging-level.enum"
import { HTMLConfig } from "src/app/interfaces/htmlConfig.interface"
import { RouteName } from "src/app/route-name"
import { IntakeFormService } from "src/app/services/intake-form/intake-form.service"
import { NewRelicService } from "src/app/services/new-relic/new-relic.service"
import { ONETAG_APPLICATION_EVENTS, OneTagService } from "src/app/services/one-tag/one-tag.service"
import { APP_CONFIG, AppConfig } from "src/app.config"
import { HshdAlertService, HshdDetection } from "src/app/services/hshd-alert/hshd-alert.service"

@Component({
  selector: "c1-privacy-intake-form-page",
  templateUrl: "./intake-form-page.component.html",
  styleUrls: ["./intake-form-page.component.scss"],
  standalone: false,
})
export class IntakeFormPageComponent implements OnInit, AfterViewInit {
  @ViewChildren("form") intakeForms: QueryList<IntakeFormComponent>
  corrections: any[] = [{ isValid: false, hasDirtyAndInvalidInputs: false }]
  violatedHSHD: { [key: string]: string[] } = {}

  subheaderContent: SubheaderContent = {
    icon: "correction",
    headerText: "INTAKE_FORM.HEADER",
    subheaderText: "INTAKE_FORM.SUBHEADER",
  }

  htmlConfig: HTMLConfig = {
    delete_request: {
      tag: "a",
      classes: "grv-text--normal-semibold small-text-on-mobile canada-link grv-color--core-blue-40",
      attributes: [
        {
          key: "routerLink",
          value: RouteName.DeleteRequest,
        },
        {
          key: "data-cy",
          value: "delete-request",
        },
      ],
      textKey: "INTAKE_FORM.DELETE_DATA_LINK",
    },
  }

  scanSDKConfig: any;

  constructor(
    public intakeFormService: IntakeFormService,
    public router: Router,
    private newRelicService: NewRelicService,
    public oneTagService: OneTagService,
    private cdr: ChangeDetectorRef,
    @Inject(APP_CONFIG) public config: AppConfig,
    private hshdAlertService: HshdAlertService
  ) {
    this.scanSDKConfig = config.scanSDKConfig;
  }

  ngOnInit(): void {
    this.oneTagService.setPageView()
    this.loadCachedFormData()
    this.newRelicService.logCustomError("User navigated to correction form from challenge app step-up", {
      event: newRelicEvent.correctionRequest,
      logLevel: ErrorLoggingLevel.Info,
    })
  }

  ngAfterViewInit(): void {
    this.setUpCachedFormData()
  }

  loadCachedFormData() {
    if (!this.intakeFormService.intakeFormData) {
      return
    }
    this.corrections = []
    this.intakeFormService.intakeFormData.forEach(d => this.addCorrection())
  }

  setUpCachedFormData() {
    for (let i = 0; i < this.corrections.length; i++) {
      this.intakeForms.get(i).setFormData(this.intakeFormService.intakeFormData[i])
    }
  }

  isFormValid(): boolean {
    let valid = true
    this.corrections.forEach((obj: any) => {
      if (!obj.isValid) {
        valid = false
      }
    })
    return valid
  }

  isFormDirty(): boolean {
    let dirty = false
    this.corrections.forEach((obj: any) => {
      if (obj.hasDirtyAndInvalidInputs) {
        dirty = true
      }
    })
    return dirty
  }

  onFormChanged(data) {
    this.corrections[data.i].isValid = data.$event.isValid
    this.corrections[data.i].hasDirtyAndInvalidInputs = data.$event.hasDirtyAndInvalidInputs
    this.cdr.detectChanges()
    this.violatedHSHD = {};
  }

  cancel() {
    this.router.navigate([RouteName.CorrectionLanding])
  }

  submit() {
    if (!this.isFormValid()) { return false }

    this.oneTagService.trackApplicationEvent(ONETAG_APPLICATION_EVENTS.linkClick, "correctionIntakeSubmit")
    this.intakeFormService.intakeFormData = []

    this.intakeForms.forEach((form) => {
      const formData = this.getFormData(form)
      if (this.scanSDKConfig?.enabled) {
        this.logFormData(formData, form.formId)
      }
      this.intakeFormService.intakeFormData.push(formData)
    })

    if (this.violatedHSHD && Object.keys(this.violatedHSHD).length > 0) {
      this.focusFirstErrorAcrossForms()
      return false;
    }

    this.router.navigate([RouteName.IntakeFormConfirmationPage], { queryParamsHandling: "merge" });
  }

  private focusFirstErrorAcrossForms(): void {
    // Defer to allow Angular to render the aria-describedby error message elements
    // before moving focus to the first errored input.
    setTimeout(() => {
      const forms = this.intakeForms.toArray()
      for (const form of forms) {
        if (form.focusFirstError()) {
          return
        }
      }
    })
  }

  private logFormData(formData: any, formId: string): void {
    this.scanViolationWithSDK("fileName", formData.fileName, formId)
    this.scanViolationWithSDK("field", formData.field, formId)
    this.scanViolationWithSDK("incorrectValue", formData.incorrectValue, formId)
    this.scanViolationWithSDK("correctValue", formData.correctValue, formId)
    this.scanViolationWithSDK("explanation", formData.explanation, formId)
  }

  private scanViolationWithSDK(controlName: string, fieldRef: any, formId: string) {
    const value = String(fieldRef);

    // Normalize em-dashes and en-dashes to regular dashes for detection
    const normalizedValue = value.replace(/[\u2014\u2013]/g, "-");

    try {
      const result: DetectionResult = detect(normalizedValue, {
        entitiesToDetect: [
          "SOCIAL_SECURITY_NUMBER",
          "CAPITAL_ONE_CREDIT_CARD",
          "NON_CAPITAL_ONE_CREDIT_CARD",
          "INDIVIDUAL_TAXPAYER_IDENTIFICATION_NUMBER",
          "PERMANENT_ACCOUNT_NUMBER",
          "NEW_PERMANENT_ACCOUNT_NUMBER",
          "BANK_ACCOUNT_NUMBER",
          "EXTERNAL_BANK_ACCOUNT_NUMBER",
          "PASSPORT_NUMBER",
          "DRIVERS_LICENSE_NUMBER",
        ]
      });

      // Guard against malformed result (missing or non-array details)
      if (!result || !Array.isArray(result.details)) {
        this.newRelicService.logCustomError("Scan SDK returned malformed result", {
          event: "SCAN_SDK_MALFORMED_RESULT",
          logLevel: ErrorLoggingLevel.Warn,
          formField: controlName,
          formId: formId,
        });
        return;
      }

      for (const detail of result.details) {
        const matchedText = normalizedValue.substring(detail.startIndex, detail.endIndex);
        const consecutiveDigits = matchedText.replace(/\D/g, "").length;

        this.addViolations(controlName, formId, [detail.entityType], consecutiveDigits);
      }
    } catch (error) {
      // Log SDK failure to New Relic but allow form to proceed (fail-open)
      this.newRelicService.logCustomError("Scan SDK threw an exception", {
        event: "SCAN_SDK_ERROR",
        logLevel: ErrorLoggingLevel.Error,
        errorMessage: error instanceof Error ? error.message : String(error),
        formField: controlName,
        formId: formId,
      });
    }
  }

  private addViolations(controlName: string, formId: string, matches: string[], consecutiveDigits: number) {
    const violations = matches.map(key => key.split("_").join(" "));
    const errorKey = `${controlName}-${formId}`;
    if (!this.violatedHSHD[errorKey]) {
      this.violatedHSHD[errorKey] = [];
    }
    this.violatedHSHD[errorKey].push(...violations);

    // Log each HSHD detection for monitoring and alerting
    matches.forEach(hshdType => {
      const detection: HshdDetection = {
        timestamp: new Date().toISOString(),
        consecutiveDigits: consecutiveDigits,
        hshdType: hshdType,
        formField: controlName,
        formId: formId,
      };
      this.hshdAlertService.logHshdDetection(detection);
    });
  }

  private getFormData(form: IntakeFormComponent): any {
    return {
      fileName: form.getValue("fileName"),
      field: form.getValue("field"),
      incorrectValue: form.getValue("incorrectValue"),
      correctValue: form.getValue("correctValue"),
      explanation: form.getValue("explanation"),
    }
  }

  addCorrection() {
    if (this.corrections.length < 5) {
      this.corrections.push({
        isValid: false,
        hasDirtyAndInvalidInputs: false,
      })
    }
  }

  onRemoveCorrection(index) {
    this.corrections.splice(index, 1)
  }

  navigateToDelete() {
    this.oneTagService.trackApplicationEvent(ONETAG_APPLICATION_EVENTS.linkClick, "correctionIntakeNavigateToDelete")
    this.router.navigate([RouteName.DeleteRequest], { queryParamsHandling: "merge" })
  }
}